import { FC, useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import useOffers from 'api/useOffers';
import { useProduct } from 'api/useProduct';
import { Offer, type ProductsSequence } from 'common/contracts';
import { TRIMMED_INPUT_REGEX } from 'constants/constants';
import { EActiveState, OfferType } from 'constants/enums';
import { ProgressBar } from 'design-system/AcSidebar/constants';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import { useTabTracking } from 'hooks/useTabTracking';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { EToasterStatus, showToast } from 'utils/showToast';
import { STATUS_OPTIONS } from 'views/CheckoutDesign/types';
import * as yup from 'yup';

import FormSectionsTabs from 'components/FormSectionsTabs';
import { Tabs } from 'components/ui/tabs';

import FormHeader from '../../components/FormHeader/FormHeader';

import ConfigurationTab from './Tabs/ConfigurationTab';
import ScheduleTab from './Tabs/ScheduleTab';
import SettingsTab from './Tabs/SettingsTab';
import { EProgressBarTabs, ProgressBarFormProps } from './types';

const ProgressBarForm: FC<ProgressBarFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { progressBarId, publisherId } = useParams();
  const navigate = useNavigate();
  const { updateOffer, addOffer, getOffer, getOffers } = useOffers({
    offerId: progressBarId,
    offerType: OfferType.PROGRESS_BAR
  });
  const { data: productsData } = useProduct(publisherId).getProducts;

  const [activeTab, setActiveTab] = useState<EProgressBarTabs>(
    EProgressBarTabs.SETTINGS
  );
  const [data, setData] = useState<Partial<Offer>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [active, setActive] = useState<EActiveState>(
    data?.active ? EActiveState.ACTIVE : EActiveState.INACTIVE
  );
  const [segment, setSegment] = useState('');
  const [productsSequence, setProductsSequence] = useState<ProductsSequence[]>(
    []
  );
  const [currentOfferUI, setCurrentOfferUI] = useState('');

  const tabTitles = Object.values(EProgressBarTabs);

  useEffect(() => {
    if (progressBarId && publisherId) {
      getOffer.refetch();
    }
  }, [progressBarId, publisherId]);

  useEffect(() => {
    if (progressBarId && !getOffer.isLoading && getOffer.data) {
      setData(getOffer.data.result);
    }
  }, [getOffer.data, getOffer.isLoading, progressBarId]);

  useEffect(() => {
    if (data) {
      setIsLoading(false);
      setCurrentOfferUI(data.offerUi?._id || '');

      const missions = data?.productsSequence?.map((product: any) => ({
        index: product.index,
        priceInUsdCents: product.priceInUsdCents ?? 0,
        barSize: product.barSize ?? 1,
        products: product?.products?.[0]
          ? [
              {
                productId:
                  product?.products[0].productId ||
                  product?.products?.[0]?.product?.productId,
                quantity: product?.products[0].quantity
              }
            ]
          : []
      })) || [{ index: 1, products: [], priceInUsdCents: 0, barSize: 1 }];
      setProductsSequence(missions);
    }
  }, [edit, data, productsData?.result]);

  const checkUniqueness = useCallback(
    (value: string | undefined, field: keyof Offer) => {
      const isTaken = getOffers.data?.offers.find((o: Offer) => {
        if (!edit) return o[field] === value;
        return o[field] === value && data[field] !== value;
      });
      return !isTaken;
    },
    [edit, data, getOffers.data]
  );

  const offerSchema = yup.object().shape({
    name: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'Name should not be spaces')
      .min(3, `Name length should be longer`)
      .required('Name is required')
      .test('uniqueness', 'Name already exists', (value) => {
        return checkUniqueness(value, 'name');
      }),
    publisherOfferId: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required('External ID is required')
      .test('uniqueness', 'External ID already exists', (value) => {
        return checkUniqueness(value, 'publisherOfferId');
      }),
    offerUiId: yup.string().required('Offer Design is required'),
    productsSequence: yup.array().of(
      yup.object().shape({
        index: yup.number().required(),
        products: yup
          .array()
          .of(
            yup.object().shape({
              productId: yup.string().required('Product is required'),
              quantity: yup
                .number()
                .typeError('Quantity must be a number')
                .required('Quantity is required')
                .min(1, 'Quantity must be at least 1')
            })
          )
          .test('valid-product', 'Product must have a quantity', (products) => {
            if (!products || products.length === 0) return true;
            return (
              products[0]?.quantity !== undefined &&
              products[0]?.quantity !== null
            );
          })
      })
    )
  });

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue
  } = useFormik({
    validateOnMount: true,
    validationSchema: offerSchema,
    enableReinitialize: true,
    initialValues: {
      publisherOfferId: data?.publisherOfferId || '',
      name: data?.name ? `${data.name}${dup ? '_copy' : ''}` : '',
      type: data?.type || OfferType.PROGRESS_BAR,
      offerUiId: data.offerUi?._id || data.offerUi?.offerUiId || '',
      active: data?.active ?? EActiveState.ACTIVE,
      segments: data?.segments || [],
      schedule: data?.schedule || { permanent: true, timeFrames: [] },
      productsSequence: productsSequence
    },
    onSubmit: async (values) => {
      const newProgressBar: Partial<Offer> & { sectionId?: string | null } = {
        ...values,
        active: active === EActiveState.ACTIVE,
        productsSequence: values.productsSequence.map((seq) => ({
          ...seq,
          products: seq?.products.map((p) => ({
            productId: p.productId,
            quantity: p.quantity
          }))
        })),
        schedule: {
          ...values.schedule,
          timeFrames: values.schedule.permanent
            ? []
            : values.schedule.timeFrames
        }
      };
      const isUpdate = edit && progressBarId && !dup;
      const action = isUpdate ? updateOffer : addOffer;
      const args: any = isUpdate
        ? { offerId: progressBarId, form: newProgressBar }
        : newProgressBar;

      await action.mutateAsync(args, {
        onSuccess: () => {
          getOffers.refetch();
          navigate('../');
          showToast({
            message: `Progress Bar ${isUpdate ? 'edited' : 'added'} successfully`,
            status: EToasterStatus.SUCCESS
          });
        },
        onError: () => {
          showToast({
            message: `Error ${isUpdate ? 'editing' : 'adding'} Progress Bar`,
            status: EToasterStatus.ERROR
          });
        }
      });
    }
  });

  useEffect(() => {
    setActive(values.active ? EActiveState.ACTIVE : EActiveState.INACTIVE);
  }, [values?.active]);

  useEffect(() => {
    setCurrentOfferUI(values.offerUiId);
  }, [values.offerUiId]);

  useUnsavedChanges({ dirty });

  const progressBarTabs = tabTitles.map((title) => ({ label: title, value: title }));

  const { handleTabChange } = useTabTracking({
    sidebarTab: ProgressBar.title,
    tabs: progressBarTabs,
    customNavigate: (value) => setActiveTab(value as EProgressBarTabs)
  });

  const formikProps = {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
    >
      <FormHeader
        onCloseForm={() => navigate('../')}
        onMainButtonClick={() => submitForm()}
        onOptionChange={(status) =>
          setFieldValue('active', status === EActiveState.ACTIVE)
        }
        selectedOption={active}
        options={STATUS_OPTIONS}
        title={`${edit ? 'Edit' : 'Create'} Progress bar`}
        isFormValid={
          !isLoading &&
          isValid &&
          (!edit || dirty) &&
          (values.schedule?.permanent || !!values.schedule?.timeFrames?.length)
        }
        mainButtonText="Save"
      />
      <div className="w-[528px] mx-auto">
        <Tabs
          className="h-full pt-10 pb-2 w-[528px]"
          value={activeTab}
          onValueChange={handleTabChange}
        >
          <FormSectionsTabs
            currentTab={activeTab}
            tabs={tabTitles.map((title) => ({
              value: title,
              label: title,
              disabled: getOffer.isLoading && edit
            }))}
          />
        </Tabs>
      </div>
      {progressBarId && getOffer.isLoading ? (
        <CircularProgress className="absolute top-1/2 left-1/2 size-16" />
      ) : (
        <div className="mt-7 pb-7 w-full mx-auto overflow-y-auto h-[calc(100vh-160px)]">
          {activeTab === EProgressBarTabs.SETTINGS && (
            <SettingsTab
              formikProps={formikProps}
              segment={segment}
              setSegment={setSegment}
              currentOfferUI={currentOfferUI}
            />
          )}

          {activeTab === EProgressBarTabs.CONFIGURATION && (
            <ConfigurationTab
              formikProps={formikProps}
              productsSequence={productsSequence}
              setProductsSequence={setProductsSequence}
            />
          )}

          {activeTab === EProgressBarTabs.SCHEDULE && (
            <ScheduleTab formikProps={formikProps} />
          )}
        </div>
      )}
    </motion.div>
  );
};

export default ProgressBarForm;
